/**
 * @module modules/query-values-sk
 * @description <h2><code>query-values-sk</code></h2>
 *
 * The right-hand side of the query-sk element, the values for a single key
 * in a query/paramset.
 *
 * @evt query-values-changed - Triggered only when the selections have actually
 *     changed. The selection is available in e.detail.
 *
 * @attr {boolean} hide_invert - If the option to invert a query should be made available to
 *       the user.
 * @attr {boolean} hide_regex - If the option to include regex in the query should be made
 *       available to the user.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "QueryValuesSk", {
    enumerable: true,
    get: function() {
        return QueryValuesSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
require("../../../elements-sk/modules/checkbox-sk");
require("../../../elements-sk/modules/multi-select-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _QueryValuesSk;
let QueryValuesSk = (_QueryValuesSk = class QueryValuesSk extends _lit.LitElement {
    // Custom setter for options to handle legacy filtering logic
    get options() {
        return this._options;
    }
    set options(val) {
        const oldVal = this._options;
        this._options = val;
        this._selected = []; // Legacy options setter resets selected
        this._fastFilter();
        this.requestUpdate('options', oldVal);
    }
    // Custom setter for selected to handle legacy cleaning and state synchronization
    get selected() {
        return this._selected;
    }
    set selected(val) {
        const oldVal = this._selected;
        this._selected = val;
        this._syncStateFromSelected();
        this.requestUpdate('selected', oldVal);
    }
    createRenderRoot() {
        return this;
    }
    _syncStateFromSelected() {
        const val = this._selected;
        this._invertChecked = !!(val.length >= 1 && val[0].startsWith('!'));
        this._regexChecked = !!(val.length === 1 && val[0].startsWith('~'));
        // Clean _selected in place
        this._selected = val.map((v)=>{
            if (v.startsWith('!') || v.startsWith('~')) {
                return v.slice(1);
            }
            return v;
        });
        if (this._selected.length && this._regexChecked) {
            this._regexValue = this._selected[0];
        }
    }
    /**
   * Filter the options displayed based on text entered in the
   * filter text box
   */ _fastFilter() {
        const filterString = this._filterString ? this._filterString.trim() : '';
        if (!filterString) {
            this._filtering = false;
            this._filteredOptions = [];
            return;
        }
        this._filtering = true;
        const filters = filterString.toLowerCase().split(/\s+/);
        const matches = (s)=>{
            s = s.toLowerCase();
            return filters.some((f)=>s.includes(f));
        };
        this._filteredOptions = this._options.filter(matches);
    }
    render() {
        // Determine which options to display (filtered or all)
        const displayOptions = this._filtering ? this._filteredOptions : this._options;
        return (0, _lit.html)`
      <checkbox-sk
        id="invert-${this.uniqueId}"
        .checked=${this._invertChecked}
        @change=${this._invertChange}
        title="Match items not selected below."
        label="Invert"
        ?hidden=${this.hide_invert}></checkbox-sk>
      <checkbox-sk
        id="regex-${this.uniqueId}"
        class="regex"
        .checked=${this._regexChecked}
        @change=${this._regexChange}
        title="Match items via regular expression."
        label="Regex"
        ?hidden=${this.hide_regex}></checkbox-sk>
      <input
        type="text"
        id="regexValue-${this.uniqueId}"
        class="regexValue"
        .value=${this._regexValue}
        @input=${this._regexInputChange} />
      <div class="filtering">
        <input
          id="filter-${this.uniqueId}"
          .value=${this._filterString}
          @input=${this._onFilterInput}
          placeholder="Filter Values"
          name="query-value-sk-filter-val"
          autocomplete="off" />
        ${this._filtering ? (0, _lit.html)`<button @click=${this.clearFilter} class="clear_filters" title="Clear filter">
              &cross;
            </button>` : ''}
      </div>
      <multi-select-sk
        id="values-${this.uniqueId}"
        class="values"
        @selection-changed=${this._selectionChange}>
        ${displayOptions.map((v)=>(0, _lit.html)` <div value=${v} ?selected=${this._selected.includes(v)}>${v}</div> `)}
      </multi-select-sk>
    `;
    }
    _invertChange(e) {
        const checkbox = e.target;
        this._invertChecked = checkbox.checked;
        if (this._invertChecked && this._regexChecked) {
            this._regexChecked = false;
        }
        this._fireEvent();
    }
    _regexChange(e) {
        const checkbox = e.target;
        this._regexChecked = checkbox.checked;
        if (this._regexChecked && this._invertChecked) {
            this._invertChecked = false;
        }
        this._fireEvent();
    }
    _onFilterInput(e) {
        this._filterString = e.target.value;
        this._fastFilter();
    }
    _regexInputChange(e) {
        this._regexValue = e.target.value;
        this._fireEvent();
    }
    _selectionChange(e) {
        const currentOptions = this._filtering ? this._filteredOptions : this._options;
        // Convert indices to values
        const newSelected = e.detail.selection.map((i)=>currentOptions[i]);
        this._selected = newSelected;
        this._fireEvent();
        this.requestUpdate();
    }
    _fireEvent() {
        const prefix = this._invertChecked ? '!' : '';
        let selectedValues = [];
        if (this._regexChecked) {
            selectedValues = [
                `~${this._regexValue}`
            ];
        } else {
            // Only include selected values that are currently visible options
            // to match legacy behavior where multi-select-sk indices are used.
            const currentOptions = this._filtering ? this._filteredOptions : this._options;
            selectedValues = this._selected.filter((v)=>currentOptions.includes(v)).map((v)=>prefix + v);
        }
        this.dispatchEvent(new CustomEvent('query-values-changed', {
            detail: {
                invert: this._invertChecked,
                regex: this._regexChecked,
                values: selectedValues
            },
            bubbles: true
        }));
    }
    clearFilter() {
        this._filterString = '';
        this._fastFilter();
    }
    constructor(...args){
        super(...args);
        this.uniqueId = `${QueryValuesSk.nextUniqueId++}`;
        this._options = [];
        this._selected = [];
        this.hide_invert = false;
        this.hide_regex = false;
        this._filtering = false;
        this._filteredOptions = [];
        this._invertChecked = false;
        this._regexChecked = false;
        this._regexValue = '';
        // We keep track of filter string to re-filter when options change
        this._filterString = '';
    }
}, (()=>{
    _QueryValuesSk.nextUniqueId = 0;
})(), _QueryValuesSk);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false,
        noAccessor: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], QueryValuesSk.prototype, "options", null);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false,
        noAccessor: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], QueryValuesSk.prototype, "selected", null);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], QueryValuesSk.prototype, "hide_invert", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], QueryValuesSk.prototype, "hide_regex", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], QueryValuesSk.prototype, "_filtering", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Array)
], QueryValuesSk.prototype, "_filteredOptions", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], QueryValuesSk.prototype, "_invertChecked", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], QueryValuesSk.prototype, "_regexChecked", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], QueryValuesSk.prototype, "_regexValue", void 0);
QueryValuesSk = _ts_decorate([
    (0, _decorators.customElement)('query-values-sk')
], QueryValuesSk);
